import * as React from 'react';
import { useState, useContext } from 'react';
import axios from "axios";
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from "react-i18next";
import config from '../config.js';
import { useSnackbar } from 'notistack';
import PasswordChecklist from "react-password-checklist"
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { AuthContext } from "../context/AuthContext";
import { sendInvitationEmail } from '../utils/sendInvitationEmail.js';
import { computeTrialExpiry } from '../utils/accessControl';

export default function NewUserDialog({ setOpen, open, handleNewUserCreated }) {

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState(false);

    const { idToken, jwt, signInAndOut } = useContext(AuthContext);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [company, setCompany] = useState("");
    const [password, setPassword] = useState("");
    const [trialWeeks, setTrialWeeks] = useState(4);

    const [passwordValid, setPasswordValid] = useState(false)

    const mystyle = {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.57,
        wordWrap: 'break-word'
    }

    const handleClose = () => {
        setName("");
        setEmail("");
        setCompany("");
        setPassword("");
        setTrialWeeks(4);
        setOpen(false);
    };

    const [checkedClassic, setCheckedClassic] = React.useState(false);
    const handleChangeClassic = (event) => {
      setCheckedClassic(event.target.checked);
    };

    const [checkedMapping, setCheckedMapping] = React.useState(true);
    const handleChangeMapping = (event) => {
      setCheckedMapping(event.target.checked);
    };

    const handleCreate = () => {
        
        if(!isLoading){

            setIsLoading(true);

            if (!checkedClassic && !checkedMapping) {
                enqueueSnackbar(`${t("warningSelectOneService")}`, {variant:'warning'} );
                setIsLoading(false);
                return;
            }

            if (!name) {
                enqueueSnackbar(`${t("warningNameNewUser")}`, {variant:'warning'} );
                setIsLoading(false);
                return;
            }

            if (!company) {
                enqueueSnackbar(`${t("warningCompanyNewUser")}`, {variant:'warning'} );
                setIsLoading(false);
                return;
            }

            // Email validation using a regular expression
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(email)) {
                enqueueSnackbar(`${t("warningEmailNewUser")}`, {variant:'warning'} );
                setIsLoading(false);
                return;
            }

            if (!passwordValid) {
                enqueueSnackbar(`${t("warningPasswordNewUser")}`, {variant:'warning'} );
                setIsLoading(false);
                return;
            }

            if (!Number.isInteger(trialWeeks) || trialWeeks < 1) {
                enqueueSnackbar(`${t("warningTrialWeeks")}`, { variant: 'warning' });
                setIsLoading(false);
                return;
            }

            setOpen(false);
            newUser(name, email, company, password, checkedClassic, checkedMapping, trialWeeks);
        }
    };

    function newUser(_name, _email, _company, _password, _checkedClassic, _checkedMapping, _trialWeeks) {

        let _service="";
        if(_checkedClassic){
            _service="classic";
        }
        if(_checkedMapping){
            _service="mapping";
        }
        if(_checkedClassic&&_checkedMapping){
            _service="all";
        }

        //console.log(service);

        const dataForm = new FormData();
        dataForm.append('data', JSON.stringify({
            name: _name,
            email: _email,
            company: _company,
            password: _password,
            service: _service
        }))
        axios.post(config.userCreationApiUrl, dataForm, {
            headers: {
                Authorization: `Bearer ${idToken}`,
            }
        }).then(res => {
            console.log(res);
            handleSendEmail(_name,_email,_password);
            signInAndOut(_email, _password).then((strapiId) => {
                //console.log("User signed in and out successfully, should now appear in CMS.");

                //updating the service field
                const nowIso = new Date().toISOString();
                const data = {
                    service: _service,
                    plan: 'trial',
                    trialWeeks: _trialWeeks,
                    planStartedAt: nowIso,
                    accessExpiresAt: computeTrialExpiry(_trialWeeks, new Date(nowIso))
                };
                axios.put(`${config.apiUrl}/api/users/${strapiId}`, data, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${jwt}`,
                    }
                })
                .then(res => {
                    console.log(res);
                })
                .catch(err => {
                    console.log(err);
                });

                handleNewUserCreated();
                setIsLoading(false);
                setName("");
                setEmail("");
                setCompany("");
                setPassword("");
                setTrialWeeks(4);
                enqueueSnackbar(`${t("successCreateNewUser")}`, {variant:'success'} );
              }).catch((err) => {
                console.error("Sign-in failed:", err);
                setIsLoading(false);
                setName("");
                setEmail("");
                setCompany("");
                setPassword("");
                setTrialWeeks(4);
                enqueueSnackbar(`${t("errorCreateNewUser")}`, {variant:'error'} );
              });
        })
        .catch(err => {
            console.log(err);
            setIsLoading(false);
            setName("");
            setEmail("");
            setCompany("");
            setPassword("");
            setTrialWeeks(4);
            enqueueSnackbar(`${t("errorCreateNewUser")}`, {variant:'error'} );
        });
    }

    const handleSendEmail = (name,email,password) => {
        sendInvitationEmail({
                name,
                email,
                password,
                token: idToken,
            }).then(res => {
                enqueueSnackbar(t('successSendInvitationEmail'), { variant: 'success' });
            })
            .catch(err => {
                console.log(err);
                enqueueSnackbar(t('errorSendInvitationEmail'), { variant: 'error' });
            });
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth={true}>
                <Box sx={{ p: 3 }}>
                    <DialogTitle>
                        <Typography variant="h4" component={"div"}>
                            {t("NewUser")}
                        </Typography>
                    </DialogTitle>
                    <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: theme.palette.grey[500],
                    })}
                    >
                    <CancelIcon />
                    </IconButton>
                    <DialogContent>
                        <DialogContentText component={"div"}>
                            <Typography
                                color="text.secondary"
                                variant="body2"
                            >
                                {t("NewUserDescription")}
                            </Typography>
                        </DialogContentText>
                        <Stack spacing={3} sx={{ mt: 3 }}>
                            <Stack
                                direction="row"
                                spacing={3}
                                sx={{ pl: 1, alignItems: 'center' }}
                            >
                            <Typography variant="body1" color="text.secondary">{t("Service")} :</Typography>
                            <FormGroup row>
                                <FormControlLabel control={<Checkbox checked={checkedClassic} onChange={handleChangeClassic} />} label={<Typography variant="body1" color="text.secondary">{t("ServiceClassic")}</Typography>} />
                                <FormControlLabel control={<Checkbox checked={checkedMapping} onChange={handleChangeMapping} />} label={<Typography variant="body1" color="text.secondary">{t("ServiceMapping")}</Typography>} />
                            </FormGroup>
                            </Stack>
                            <TextField
                                fullWidth
                                label={t("TrialPeriodWeeks")}
                                name="TrialWeeks"
                                type="number"
                                value={trialWeeks}
                                onChange={(event) => {
                                    const v = parseInt(event.target.value, 10);
                                    setTrialWeeks(Number.isNaN(v) ? '' : v);
                                }}
                                helperText={t("TrialPeriodHelp")}
                                slotProps={{ htmlInput: { min: 1, max: 104, step: 1 } }}
                            />
                            <TextField
                                fullWidth
                                label={t("Name")}
                                name="Name"
                                type="text"
                                value={name}
                                onChange={(event) => { setName(event.target.value) }}
                            />
                            <TextField
                                fullWidth
                                label={t("Email")}
                                name="Email"
                                type="text"
                                value={email}
                                onChange={(event) => { setEmail(event.target.value) }}
                            />
                            <TextField
                                fullWidth
                                label={t("Company")}
                                name="Company"
                                type="text"
                                value={company}
                                onChange={(event) => { setCompany(event.target.value) }}
                            />
                            <TextField
                                fullWidth
                                label={t("Password")}
                                name="Password"
                                type="text"
                                value={password}
                                onChange={(event) => { setPassword(event.target.value) }}
                            />
                           <Box sx={{ pl: 1, pb:1 }}>
                                <PasswordChecklist
                                    style={mystyle}
                                    rules={["minLength", "specialChar", "number", "capital", "lowercase"]}
                                    minLength={8}
                                    value={password} 
                                    messages={{
                                        minLength: t("minLengthPW"),
                                        specialChar: t("specialCharPW"),
                                        number: t("numberPW"),
                                        capital: t("capitalPW"),
                                        lowercase: t("lowercasePW"),
                                    }}
                                    onChange={(isValid) => { setPasswordValid(isValid) }}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                       <Button variant="contained" onClick={handleCreate}>{isLoading ? `${t("Creating")} ...` : `${t("Create")}`}</Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
}
